import { isEmpty, isNil } from "ramda";
import { interpolateRgb } from "d3";
import { LegendContainer } from "./styled";

export const shouldAddPointsTotal = ({
  indexes,
  index,
  pk,
  difference,
  value,
  prevValues,
  filteredPoints,
  temporalIndexes,
  visualIndexes,
}) => {
  if (
    indexes.includes(index) ||
    Math.abs(value - prevValues?.values?.[0]) > difference ||
    temporalIndexes.includes(pk) ||
    visualIndexes.includes(index)
  ) {
    prevValues.values = [value];
    if (visualIndexes.includes(index) || temporalIndexes.includes(pk) || isEmpty(temporalIndexes)) {
      prevValues.withColor = true;
    } else {
      prevValues.withColor = false;
    }
    return true;
  } else {
    // filteredPoints[filteredPoints.length - 1].measuresValue = Math.max(value, prevValues?.values?.[0]);
  }
  return false;
};
export const getMeasure = (measures, min, max, thresholds, domain) => {
  if (!Array.isArray(domain) || domain.length === 0) {
    return 0;
  }

  const minValue = min;
  const maxValue = max;
  const range = maxValue - minValue;

  // Caso borde
  if (range === 0) {
    return 0;
  }

  let result = 0;

  // Thresholds (como en tu lógica original: todos = max)
  const th = Array(domain.length).fill(maxValue);

  // Recorremos de mayor a menor
  for (let j = domain.length - 1; j >= 0; j--) {
    if (th[j] <= measures) {
      if (result < domain[j]) {
        result = domain[j];
      }

      // early return (igual que tú)
      if (j === domain.length - 1) {
        return result;
      }
    }
  }

  // Si no entra en thresholds → normalización
  if (measures < th[0]) {
    let temp = (measures - minValue) / range;

    // 🔑 FIX universal (sin Math.abs)
    temp = Math.max(0, Math.min(temp, 1));

    if (result < temp) {
      result = temp;
    }
  }

  return result;
};

export const Legend = ({ assetData, equipmentData, measureName, percentiles, scales, type, intl }) => {
  const { colorSteps, positionsLetters, positionsColors, bounds } = createColor({
    assetData,
    measureName,
    percentiles,
    equipmentData,
    type,
    intl,
  });

  const legendItems = Array.from({ length: colorSteps }, (_, index) => {
    const value = index * (1 / colorSteps);

    const interpolation = !isNil(scales[measureName])
      ? scales[measureName]
      : type === "zones"
        ? scales["zones"]
        : scales["temperature"];
    const color = interpolation(value);
    return { value, color };
  });

  return (
    <LegendContainer key={`${measureName}_legend`}>
      <svg width="100%" height={"11.8rem"}>
        <defs>
          <linearGradient id={`mapGradient_${measureName}`} x1="0%" y1="0%" x2="0%" y2="100%">
            {legendItems.toReversed().map((item, index) => (
              <stop key={`offset_${measureName}_${index}`} offset={positionsColors[index]} stopColor={item.color} />
            ))}
          </linearGradient>
        </defs>
        <rect fill={`url(#mapGradient_${measureName})`} x="0" y="10" rx="20" ry="20" width="30%" height={"18vh"} />
        {bounds.toReversed().map((bound, index) => {
          const lines = splitText(bound, 12);

          return (
            <text
              key={`text_${measureName}_${bound}_${index}`}
              x="35%"
              y={positionsLetters[index]}
              textAnchor="start"
              fill="black"
            >
              {lines.map((line, i) => (
                <tspan key={i} x="35%" dy={i === 0 ? "0" : "1.2em"}>
                  {line}
                </tspan>
              ))}
            </text>
          );
        })}
      </svg>
    </LegendContainer>
  );
};
export const createColor = ({ assetData, measureName, percentiles, scales, type, equipmentData, intl }) => {
  if (!isNil(equipmentData)) {
    let colorSteps = 0;
    //const measure_index = equipmentData.pMeasureNames?.indexOf(measureName);
    //const threshold = equipmentData.pThresholds?.[measure_index];
    let positionsLetters = ["13%"];
    let positionsColors = [];
    let domain = [];
    let range = [];
    let max = 1;
    let min = 0;
    let bounds;
    if (!isNil(percentiles) && !isNil(percentiles[measureName]) && type !== "zones") {
      max = percentiles[measureName][1];
      min = percentiles[measureName][0];
      const diff = max - min;
      if (diff <= 3) {
        bounds = [
          Math.round(min * 10) / 10,
          Math.round((min + diff / 4) * 10) / 10,
          Math.round((min + diff / 2) * 10) / 10,
          Math.round((max - diff / 4) * 10) / 10,
          Math.round(max * 10) / 10,
        ];
      } else {
        bounds = [
          Math.round(min),
          Math.round(min + diff / 4),
          Math.round(min + diff / 2),
          Math.round(max - diff / 4),
          Math.round(max),
        ];
      }

      //if (threshold.length < 3 && measureName !== "temperature" && measureName !== "strain") {
      //  bounds.splice(0, threshold.length === 2 ? 1 : 2);
      //}
      domain = [0, 0.2, 0.5, 0.8];
      positionsLetters.push("35%");
      positionsLetters.push("55%");
      positionsLetters.push("75%");
      positionsLetters.push("95%");
      positionsColors = ["0%", "20%", "30%", "40%", "50%", "70%", "80%", "90%"];
    } else {
      bounds = [intl.formatMessage({ id: "OK" })]; //unburied

      //if (threshold.length < 3) {
      //  bounds.splice(0, threshold.length === 2 ? 1 : 2);
      //}
      bounds.push(intl.formatMessage({ id: "warning" })); //"partially.unburied"
      bounds.push(intl.formatMessage({ id: "risk" })); //"buried"
      positionsLetters.push("55%");
      positionsLetters.push("93%");
      domain = [0, 0.45, 0.8];
      range = [interpolateRgb("green")(0), interpolateRgb("yellow")(0.4), interpolateRgb("red")(0.8)];
      positionsColors = ["10%", "50%", "90%"];
    }

    colorSteps = positionsColors.length;
    return { colorSteps, range, positionsLetters, positionsColors, domain, min, max, /*threshold,*/ bounds };
  } else {
    return {
      colorSteps: null,
      range: null,
      positionsLetters: null,
      positionsColors: null,
      domain: null,
      min: null,
      max: null,
      threshold: null,
      bounds: null,
    };
  }
};
const splitText = (text, maxChars = 10) => {
  const words = String(text).split(" ");
  const lines = [];
  let currentLine = "";

  words.forEach((word) => {
    if ((currentLine + word).length > maxChars) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine += (currentLine ? " " : "") + word;
    }
  });

  if (currentLine) lines.push(currentLine);

  return lines;
};
